import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { useState } from 'react';
import { UpdateType } from 'src/constants/portfolio';
import { graphql, unmaskFragment } from 'src/gql';
import { assetDeleteButtonText, assetDeletionText } from 'src/utils/asset';

import type { AssetClassName } from '@savvy/constants/generated/gql-enums';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';
import defaultGuard from '@savvy/utils/default-guard';

import RemoveItemDialog from '../views/remove-item-dialog';
import PortfolioAssetDescriptionRow from './portfolio-asset-description-row';

export const RemoveAssetDialogFragment = graphql(/* GraphQL */ `
  fragment RemoveAssetDialog on Asset {
    ...PortfolioAssetDescriptionRow
    id
    assetClass
  }
`);

const DELETE_ASSET = graphql(/* GraphQL */ `
  mutation DeleteAsset($input: DeleteAssetInput!) {
    deleteAsset(input: $input) {
      success
    }
  }
`);

export default function RemoveAssetDialog({
  assetFrag,
  onRemove,
  onClose,
}: {
  readonly assetFrag: FragmentType<typeof RemoveAssetDialogFragment>;
  readonly onRemove: Deprecated_HandleItemUpdate;
  readonly onClose: () => void;
}) {
  const asset = unmaskFragment(RemoveAssetDialogFragment, assetFrag);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteAsset] = useMutationWithErrorLogging(DELETE_ASSET);

  const handleDeleteClick = async () => {
    setIsLoading(true);
    await deleteAsset({
      variables: {
        input: {
          id: asset.id,
        },
      },
    });

    setIsLoading(false);
    onRemove({
      type: updateTypeForAssetClass(asset.assetClass),
      id: asset.id,
    });
  };

  return (
    <RemoveItemDialog
      descriptionRow={<PortfolioAssetDescriptionRow assetFrag={asset} />}
      deleteButtonText={assetDeleteButtonText(asset.assetClass)}
      deletionText={assetDeletionText(asset.assetClass)}
      isLoading={isLoading}
      onClose={onClose}
      onRemove={handleDeleteClick}
    />
  );
}

function updateTypeForAssetClass(assetClass: AssetClassName) {
  switch (assetClass) {
    case 'company_equity':
      return UpdateType.DELETE_COMPANY_EQUITY;
    case 'real_estate':
      return UpdateType.DELETE_REAL_ESTATE;
    case 'collectible':
    case 'insurance_policy':
    case 'other':
    case 'vehicle':
      return UpdateType.DELETE_ASSET;
    default:
      return defaultGuard(assetClass, UpdateType.DELETE_ASSET);
  }
}
